/**
 * Env diagnostics admin hooks (#266).
 *
 * Mirrors the two endpoints in `api/src/env-admin/env-admin.routes.ts`:
 *
 *   - GET  /api/admin/env          — per-var status snapshot
 *   - POST /api/admin/env/reveal   — single-secret reveal + audit row
 *
 * The list response is fetched fresh on every page mount (no
 * `staleTime`) so a credential rotation followed by a deploy reflects
 * immediately. The reveal mutation deliberately does NOT cache the
 * plaintext value in TanStack Query — the page owns the per-row 10-second
 * timer + plaintext state in component-local `useState`, never in
 * persistent query cache.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";

/**
 * Wire shape returned by `GET /api/admin/env`. Mirror of the API's
 * `EnvVarStatus` type. Kept as a web-local declaration (not in shared/)
 * because the schema itself lives server-side per issue #266 — the SPA
 * only consumes the wire DTO, never the schema metadata directly.
 */
export type EnvVarWireGroup =
  | "runtime"
  | "database"
  | "auth"
  | "ai"
  | "image"
  | "public"
  | "cookies"
  | "analytics"
  | "geoip";

export type EnvVarWireStatus = "ok" | "missing" | "placeholder" | "using-default";

export type EnvVarWire = {
  name: string;
  required: boolean;
  secret: boolean;
  defaultValue?: string;
  description: string;
  group: EnvVarWireGroup;
  status: EnvVarWireStatus;
  /**
   * The value shown in the list. For secret + status=ok this is masked
   * (`first4…last4 (Nchars)`). For placeholder it is the raw text so the
   * operator can see what they left in place. For missing it is the
   * empty string.
   */
  value: string;
};

/**
 * Query key for the env-diagnostics list. Co-located with the hook so
 * mutations invalidate the right key without import gymnastics. Not
 * threaded through `queryKeys.ts` because this is the only consumer.
 */
export const envDiagnosticsQueryKey = ["envDiagnostics", "list"] as const;

export function useEnvDiagnostics() {
  return useQuery<EnvVarWire[], ApiClientError>({
    queryKey: envDiagnosticsQueryKey,
    queryFn: () => apiGet<EnvVarWire[]>("/api/admin/env"),
  });
}

export type RevealResult = { value: string };

export function useRevealEnvVar() {
  return useMutation<RevealResult, ApiClientError, { name: string }>({
    mutationFn: (input) => apiPost<RevealResult>("/api/admin/env/reveal", input),
    // No cache invalidation — the list endpoint's masked value is still
    // current after a reveal. The page owns the local plaintext state.
  });
}
